// src/components/UpdatesPanel.jsx

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useSocket } from './SocketContext';
import {
    FaGift, FaTimes, FaTools, FaRocket, FaWrench, FaBug,
    FaLightbulb, FaThumbsUp, FaThumbsDown, FaStar,
    FaRegCommentDots, FaChevronRight, FaFire
} from 'react-icons/fa';
import DOMPurify from 'dompurify';
import './UpdatesPanel.css';

// ─── Fix da imagem quebrada ───────────────────────────────────────────────────
// Se imagem_url for relativa (ex: "/uploads/..."), prefixamos o host da API.
// Se já for absoluta (http/https) ou base64 (data:), usa como está.
const resolveAssetUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) {
        return url;
    }
    // Tira o sufixo "/api" do baseURL para chegar na raiz do servidor
    const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

// ─── Configuração de categorias ───────────────────────────────────────────────
const CAT = {
    NOVO:     { icon: <FaRocket />,  label: 'Novidade',   cls: 'up-cat--novo'     },
    MELHORIA: { icon: <FaWrench />,  label: 'Melhoria',   cls: 'up-cat--melhoria' },
    CORRECAO: { icon: <FaBug />,     label: 'Correção',   cls: 'up-cat--correcao' },
    GMUD:     { icon: <FaTools />,   label: 'Manutenção', cls: 'up-cat--gmud'     },
};

const getCategory = (update) => {
    if (update.tipo === 'gmud') return CAT.GMUD;
    return CAT[update.categoria] || CAT.MELHORIA;
};

const STATUS_BADGE = {
    EM_DESENVOLVIMENTO: { label: '⚙️ Desenvolvendo',  cls: 'sug-badge--dev'  },
    CONCLUIDA:          { label: '✅ Concluída',       cls: 'sug-badge--done' },
    REJEITADA:          { label: '🚫 Encerrada',       cls: 'sug-badge--off'  },
};

// ─── Card de atualização ──────────────────────────────────────────────────────
const UpdateCard = ({ update, index }) => {
    const cat = getCategory(update);
    const imgSrc = resolveAssetUrl(update.imagem_url);

    return (
        <li className={`up-card ${cat.cls}`} style={{ animationDelay: `${index * 50}ms` }}>
            <div className="up-card__header">
                <div className={`up-card__badge ${cat.cls}`}>
                    {cat.icon}
                    <span>{cat.label}</span>
                    {update.versao && <span className="up-card__version">{update.versao}</span>}
                </div>
                <time className="up-card__date">
                    {new Date(update.data_publicacao).toLocaleDateString('pt-BR', {
                        day: '2-digit', month: 'short', year: 'numeric'
                    })}
                </time>
            </div>

            <h4 className="up-card__title">{update.titulo}</h4>

            {imgSrc && (
                <div className="up-card__img-wrap">
                    <img
                        src={imgSrc}
                        alt={update.titulo}
                        loading="lazy"
                        className="up-card__img"
                        onError={e => { e.currentTarget.closest('.up-card__img-wrap').style.display = 'none'; }}
                    />
                </div>
            )}

            <div
                className="up-card__desc"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.descricao) }}
            />
        </li>
    );
};

// ─── Card de sugestão ─────────────────────────────────────────────────────────
const SuggestionCard = ({ sug, onVote, index }) => {
    const votos  = Number(sug.votos) || 0;
    const isHot  = votos >= 5;
    const badge  = STATUS_BADGE[sug.status];
    const scoreClass = votos > 0 ? 'positive' : votos < 0 ? 'negative' : '';

    return (
        <div className="sug-card" style={{ animationDelay: `${index * 40}ms` }}>
            {/* Placar de votos */}
            <div className="sug-card__score">
                <span className={`sug-score-num ${scoreClass}`}>{votos}</span>
                <span className="sug-score-label">pts</span>
                {isHot && <FaFire className="sug-hot" title="Em alta!" />}
            </div>

            {/* Conteúdo */}
            <div className="sug-card__body">
                <div className="sug-card__meta">
                    <span className="sug-card__author">{sug.autor_nome}</span>
                    <span className="sug-card__company">· {sug.empresa_nome}</span>
                    {badge && (
                        <span className={`sug-badge ${badge.cls}`}>{badge.label}</span>
                    )}
                </div>

                <p className="sug-card__text">{sug.texto}</p>

                <div className="sug-card__actions">
                    {/* Upvote */}
                    <button
                        className={`sug-vote-btn sug-vote-btn--up ${sug.user_vote_type === 'UP' ? 'active' : ''}`}
                        onClick={() => onVote(sug.id, 'UP')}
                        title="Gostei"
                    >
                        <FaThumbsUp />
                    </button>

                    {/* Downvote */}
                    <button
                        className={`sug-vote-btn sug-vote-btn--down ${sug.user_vote_type === 'DOWN' ? 'active' : ''}`}
                        onClick={() => onVote(sug.id, 'DOWN')}
                        title="Não curti"
                    >
                        <FaThumbsDown />
                    </button>

                    {/* Super like */}
                    <button
                        className={`sug-super-btn ${sug.user_vote_type === 'SUPER' ? 'active' : ''}`}
                        onClick={() => onVote(sug.id, 'SUPER')}
                        title="Super Like — vale +2 pts, único por usuário"
                    >
                        <FaStar /> Super Like
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Componente principal ─────────────────────────────────────────────────────
export default function UpdatesPanel() {
    const [isOpen, setIsOpen]             = useState(false);
    const [activeTab, setActiveTab]       = useState('updates');

    const [updates, setUpdates]           = useState([]);
    const [loadingUpdates, setLoading]    = useState(true);
    const [hasNew, setHasNew]             = useState(false);

    const [suggestions, setSuggestions]   = useState([]);
    const [loadingSugg, setLoadingSugg]   = useState(true);
    const [newText, setNewText]           = useState('');
    const [submitting, setSubmitting]     = useState(false);

    const socket = useSocket();

    // ── Fetch updates ──
    const fetchUpdates = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/api/system-hub/updates');
            const list = data.items || (Array.isArray(data) ? data : []);
            setUpdates(list);
            const lastId = parseInt(localStorage.getItem('lastSeenUpdateId') || '0', 10);
            if (list.length > 0 && list[0].id > lastId) setHasNew(true);
        } catch (e) {
            console.error('Erro ao carregar atualizações:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Fetch suggestions ──
    const fetchSuggestions = useCallback(async () => {
        try {
            setLoadingSugg(true);
            const { data } = await api.get('/api/system-hub/suggestions');
            setSuggestions(data.items || (Array.isArray(data) ? data : []));
        } catch (e) {
            console.error('Erro ao carregar sugestões:', e);
        } finally {
            setLoadingSugg(false);
        }
    }, []);

    useEffect(() => {
        fetchUpdates();
        fetchSuggestions();
    }, [fetchUpdates, fetchSuggestions]);

    // ── Socket ──
    useEffect(() => {
        if (!socket) return;
        const onNew   = (d) => { if (d?.id) { setUpdates(p => [d, ...p]); setHasNew(true); } };
        const onSugg  = () => fetchSuggestions();

        socket.on('new_system_update',         onNew);
        socket.on('new_suggestion',            onSugg);
        socket.on('suggestion_voted',          onSugg);
        socket.on('suggestion_status_changed', onSugg);
        return () => {
            socket.off('new_system_update',         onNew);
            socket.off('new_suggestion',            onSugg);
            socket.off('suggestion_voted',          onSugg);
            socket.off('suggestion_status_changed', onSugg);
        };
    }, [socket, fetchSuggestions]);

    // ── Toggle painel ──
    const toggle = () => {
        setIsOpen(o => !o);
        if (!isOpen && hasNew && updates.length > 0) {
            setHasNew(false);
            localStorage.setItem('lastSeenUpdateId', String(updates[0].id));
        }
    };

    // ── Enviar sugestão ──
    const submitSuggestion = async (e) => {
        e.preventDefault();
        if (!newText.trim()) return;
        try {
            setSubmitting(true);
            await api.post('/api/system-hub/suggestions', { texto: newText });
            setNewText('');
            fetchSuggestions();
        } catch (err) {
            alert(err.response?.data?.error || 'Erro ao enviar ideia.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Votar ──
    const handleVote = async (id, tipo_voto) => {
        try {
            await api.post(`/api/system-hub/suggestions/${id}/vote`, { tipo_voto });
            fetchSuggestions();
        } catch (err) {
            alert(err.response?.data?.error || 'Erro ao processar voto.');
        }
    };

    return (
        <>
            {/* ── Aba lateral ── */}
            <div
                className={`up-tab${hasNew ? ' up-tab--new' : ''}`}
                onClick={toggle}
                title="Novidades & Ideias"
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && toggle()}
            >
                <FaGift />
                <span>NOVIDADES</span>
                {hasNew && <span className="up-tab__dot" />}
            </div>

            {/* ── Backdrop ── */}
            {isOpen && <div className="up-backdrop" onClick={toggle} />}

            {/* ── Painel ── */}
            <div className={`up-panel${isOpen ? ' up-panel--open' : ''}`}>

                {/* Cabeçalho */}
                <div className="up-panel__header">
                    <div className="up-panel__header-left">
                        <div className="up-panel__icon"><FaGift /></div>
                        <div>
                            <p className="up-panel__eyebrow">Sistema</p>
                            <h3 className="up-panel__title">System Hub</h3>
                        </div>
                    </div>
                    <button className="up-panel__close" onClick={toggle} title="Fechar">
                        <FaTimes />
                    </button>
                </div>

                {/* Abas */}
                <div className="up-panel__tabs">
                    <button
                        className={`up-panel__tab${activeTab === 'updates' ? ' up-panel__tab--active' : ''}`}
                        onClick={() => setActiveTab('updates')}
                    >
                        <FaRocket /> Atualizações
                        {hasNew && <span className="up-tab-badge" />}
                    </button>
                    <button
                        className={`up-panel__tab${activeTab === 'suggestions' ? ' up-panel__tab--active up-panel__tab--orange' : ''}`}
                        onClick={() => setActiveTab('suggestions')}
                    >
                        <FaLightbulb /> Ideias
                    </button>
                </div>

                {/* Conteúdo */}
                <div className="up-panel__body">

                    {/* ── Tab: Atualizações ── */}
                    {activeTab === 'updates' && (
                        loadingUpdates ? (
                            <div className="up-state">
                                <div className="up-spinner" />
                                <p>Carregando novidades…</p>
                            </div>
                        ) : updates.length === 0 ? (
                            <div className="up-state">
                                <FaRocket className="up-state__icon" />
                                <p>Nenhuma atualização ainda.</p>
                            </div>
                        ) : (
                            <ul className="up-list">
                                {updates.map((u, i) => (
                                    <UpdateCard key={u.id} update={u} index={i} />
                                ))}
                            </ul>
                        )
                    )}

                    {/* ── Tab: Sugestões ── */}
                    {activeTab === 'suggestions' && (
                        <div className="sug-area">

                            {/* Formulário */}
                            <div className="sug-form-card">
                                <div className="sug-form-card__top">
                                    <FaRegCommentDots />
                                    <p>Tem uma ideia para o sistema?</p>
                                </div>
                                <form onSubmit={submitSuggestion} className="sug-form">
                                    <textarea
                                        className="sug-textarea"
                                        placeholder="Descreva sua ideia de melhoria ou nova funcionalidade…"
                                        value={newText}
                                        onChange={e => setNewText(e.target.value)}
                                        rows={3}
                                        required
                                    />
                                    <button
                                        type="submit"
                                        className="sug-submit-btn"
                                        disabled={submitting || !newText.trim()}
                                    >
                                        {submitting ? 'Enviando…' : <>Compartilhar <FaChevronRight /></>}
                                    </button>
                                </form>
                            </div>

                            {/* Lista */}
                            {loadingSugg ? (
                                <div className="up-state">
                                    <div className="up-spinner up-spinner--orange" />
                                    <p>Carregando ideias…</p>
                                </div>
                            ) : suggestions.length === 0 ? (
                                <div className="up-state">
                                    <FaLightbulb className="up-state__icon up-state__icon--orange" />
                                    <p>Seja o primeiro a compartilhar uma ideia!</p>
                                </div>
                            ) : (
                                <div className="sug-list">
                                    {suggestions.map((s, i) => (
                                        <SuggestionCard
                                            key={s.id}
                                            sug={s}
                                            index={i}
                                            onVote={handleVote}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}